import BaseRepository from '@/api/BaseRepository';

export default class ReportsRepository extends BaseRepository {
    public getOverviewStats(): Promise<any> {
        return this.axiosClient.get('/reports/overview');
    }

    public getQuotationBreakdown(params?: { year?: number; quarter?: number }): Promise<any> {
        const search = new URLSearchParams();
        if (params?.year != null) {
            search.append('year', String(params.year));
        }
        if (params?.quarter != null) {
            search.append('quarter', String(params.quarter));
        }
        const q = search.toString();
        return this.axiosClient.get(`/reports/overview/quotation-breakdown${q ? `?${q}` : ''}`);
    }

    public getTopLabels(year?: number, limit = 50): Promise<any> {
        const params = new URLSearchParams();
        if (year) params.append('year', String(year));
        params.append('limit', String(limit));
        return this.axiosClient.get(`/reports/labels/top?${params}`);
    }

    public getLabelsByYear(): Promise<any> {
        return this.axiosClient.get('/reports/labels/by-year');
    }

    public getLabelStats(): Promise<any> {
        return this.axiosClient.get('/reports/labels/stats');
    }

    public getActuatorStats(): Promise<any> {
        return this.axiosClient.get('/reports/actuator-stats');
    }

    public getActuatorTypes(): Promise<any> {
        return this.axiosClient.get('/reports/actuator-types');
    }

    public getActuatorTypeModels(type: string): Promise<any> {
        return this.axiosClient.get(`/reports/actuator-type-models?type=${encodeURIComponent(type)}`);
    }

    public getActuatorTypeModelStations(type: string, label: string): Promise<any> {
        const params = new URLSearchParams({ type, label });
        return this.axiosClient.get(`/reports/actuator-type-model-stations?${params}`);
    }

    public getActuatorTypesByMonth(): Promise<any> {
        return this.axiosClient.get('/reports/actuator-types/by-month');
    }

    public getProjectStats(): Promise<any> {
        return this.axiosClient.get('/reports/projects/stats');
    }

    /**
     * Aggregated sales funnel + sizing statistics for the Projects report.
     * See docs/reports-funnel-overview-api.md for the response contract.
     */
    public getProjectFunnelOverview(): Promise<any> {
        return this.axiosClient.get('/reports/projects/funnel-overview');
    }

    public getProjectAnalytics(): Promise<any> {
        return this.axiosClient.get('/reports/projects/analytics');
    }

    public getProjectsByCustomer(): Promise<any> {
        return this.axiosClient.get('/reports/projects/by-customer');
    }

    public getCustomerStats(): Promise<any> {
        return this.axiosClient.get('/reports/customers/stats');
    }

    public getCustomerAnalytics(params?: {
        from?: number;
        to?: number;
        scope?: 'period' | 'kpi' | 'status' | 'top-volume' | 'top-projects' | 'directory' | 'project-activity';
        includeRows?: boolean;
        page?: number;
        limit?: number;
        q?: string;
        sort?: string;
        dir?: 'asc' | 'desc';
    }): Promise<any> {
        const search = new URLSearchParams();
        if (params?.from != null) {
            search.append('from', String(params.from));
        }
        if (params?.to != null) {
            search.append('to', String(params.to));
        }
        if (params?.scope) {
            search.append('scope', params.scope);
        }
        if (params?.includeRows === false) {
            search.append('includeRows', 'false');
        }
        if (params?.page != null) {
            search.append('page', String(params.page));
        }
        if (params?.limit != null) {
            search.append('limit', String(params.limit));
        }
        if (params?.q) {
            search.append('q', params.q);
        }
        if (params?.sort) {
            search.append('sort', params.sort);
        }
        if (params?.dir) {
            search.append('dir', params.dir);
        }
        const q = search.toString();
        return this.axiosClient.get(`/reports/customers/analytics${q ? `?${q}` : ''}`);
    }

    public getCustomerAnalyticsDetail(customerId: string): Promise<any> {
        return this.axiosClient.get(`/reports/customers/${encodeURIComponent(customerId)}/analytics`);
    }

    public getTechnicalStats(): Promise<any> {
        return this.axiosClient.get('/reports/technical/stats');
    }
}
